import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Radio } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";

import {
  SummitSessionCard,
  SummitDayAccordion,
  ICalCalendarIcon,
} from "~/components/summit";
import { SummitLifetimeAccessBanner } from "~/components/summit/summit-lifetime-access-banner";
import { SummitLiveStream } from "~/components/summit/summit-live-stream";
import { CDNImage, getCDNImageUrl } from "~/components/ui/cdn-image";
import { Footer } from "~/components/ui/footer";
import ImageDcmLogoHorizontalWhiteUrl from "~/img/logo/dcm-logo-horizontal-white.png";
import { getPublicSummitDataOptions } from "~/lib/functions/summit";
import { createBrandMeta } from "~/lib/shared/meta";
import type { AgendaAssignment } from "~/lib/shared/summit-ab-test";
import {
  useActiveLiveStream,
  useLiveSummitCTA,
  type SessionFooterVariant,
} from "~/lib/summit";
import { getAppUrl } from "~/lib/utils";

/**
 * Appends A/B test UTM parameters to a URL. A visitor outside the test gets
 * none, so the test's results count only its participants.
 */
function appendAgendaTestUtm(url: string, agenda: AgendaAssignment): string {
  if (!agenda.inTest) return url;
  const parsed = new URL(url, getAppUrl());
  parsed.searchParams.set("utm_campaign", "summit_agenda_test");
  parsed.searchParams.set("utm_content", agenda.variant);
  return parsed.toString();
}

const DaySearchSchema = z
  .object({
    live_id: z.string().optional(),
  })
  .passthrough();

export const Route = createFileRoute("/summit/day/$number")({
  validateSearch: (search) => DaySearchSchema.parse(search ?? {}),
  params: {
    parse: (params) => ({
      number: z.coerce.number().int().positive().parse(params.number),
    }),
    stringify: (params) => ({ number: String(params.number) }),
  },
  loader: async ({ context, params }) => {
    const summitData = await context.queryClient.ensureQueryData(
      getPublicSummitDataOptions,
    );

    if (!summitData) {
      throw notFound();
    }

    // Days are sorted by date, so 1-indexed day number maps to index (number - 1)
    const sortedDays = [...summitData.days].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    const dayIndex = params.number - 1;
    if (dayIndex < 0 || dayIndex >= sortedDays.length) {
      throw notFound();
    }

    // Assign A/B test variant (persisted in cookie with analytics consent)
    const { getOrAssignAgendaVariant } =
      await import("~/lib/functions/summit-ab-test");
    const agenda = await getOrAssignAgendaVariant();

    return { summitData, dayNumber: params.number, agenda };
  },
  head: ({ loaderData, params }) => {
    const { summitData, dayNumber } = loaderData ?? {};

    if (!summitData) {
      return createBrandMeta({
        title: `Summit Day ${params.number}`,
        description:
          "Watch the Dream Coach Match Summit live stream and learn from industry leaders and master coaches.",
        ogType: "website",
      });
    }

    const description = `Watch ${summitData.summit.title} Day ${dayNumber} live. Join industry leaders and master coaches for an immersive learning experience.`;

    return createBrandMeta({
      title: `${summitData.summit.title} - Day ${dayNumber}`,
      description,
      ogTitle: `${summitData.summit.title} Day ${dayNumber} | Dream Coach Match`,
      ogDescription: description,
      ogImage: getCDNImageUrl(
        "https://form-data.dreamcoachmatch.com/summit/summit-bg.jpg",
        1200,
      ),
      ogType: "website",
      ogUrl: `https://dreamcoachmatch.com/summit/day/${dayNumber}`,
      twitterCard: "summary_large_image",
      twitterTitle: `${summitData.summit.title} Day ${dayNumber}`,
      twitterDescription: description,
      twitterImage: getCDNImageUrl(
        "https://form-data.dreamcoachmatch.com/summit/summit-bg.jpg",
        1200,
      ),
      canonical: `https://dreamcoachmatch.com/summit/day/${dayNumber}`,
      keywords: [
        "summit",
        "live stream",
        "coaching",
        "professional development",
        "master coaches",
      ],
      author: "Dream Coach Match",
    });
  },
  component: SummitDayPage,
});

interface StreamSession {
  startTimeUtc: Date | string;
  title: string;
  sessionFormat?: string;
  liveUrl?: string;
  durationMinutes: number;
}

function getNextLiveSessionForDay(
  sessions: StreamSession[],
): StreamSession | null {
  const now = new Date();

  // Filter to keynote/presentation sessions with live URLs, then find the next one
  const upcomingSessions = sessions
    .filter(
      (s) =>
        s.liveUrl &&
        (s.sessionFormat === "keynote" || s.sessionFormat === "presentation"),
    )
    .map((s) => ({
      ...s,
      startTime:
        s.startTimeUtc instanceof Date
          ? s.startTimeUtc
          : new Date(s.startTimeUtc),
    }))
    .filter((s) => s.startTime > now)
    .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

  return upcomingSessions[0] ?? null;
}

function formatNextSessionTime(date: Date): string {
  return date.toLocaleString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function StreamWaitingMessage({
  sessions,
}: {
  readonly sessions: StreamSession[];
}): React.ReactElement {
  const nextSession = getNextLiveSessionForDay(sessions);

  return (
    <div className="mx-auto max-w-5xl px-4 md:px-6">
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm md:p-12">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-purple-100">
          <Radio className="h-8 w-8 text-purple-600" />
        </div>
        <h2 className="mb-2 font-heading text-xl font-semibold text-gray-900 md:text-2xl">
          {nextSession ? "Stream Starting Soon" : "No Live Stream Right Now"}
        </h2>
        {nextSession ? (
          <div className="space-y-1 text-gray-600">
            <p className="font-medium text-gray-800">{nextSession.title}</p>
            <p className="text-sm">
              {formatNextSessionTime(
                nextSession.startTimeUtc instanceof Date
                  ? nextSession.startTimeUtc
                  : new Date(nextSession.startTimeUtc),
              )}
            </p>
            <p className="mt-3 text-sm text-gray-500">
              This page will automatically update when the stream begins.
            </p>
          </div>
        ) : (
          <p className="text-gray-600">
            Check the agenda below for session details.
          </p>
        )}
      </div>
    </div>
  );
}

function SummitDayPage() {
  const { live_id: liveIdOverride } = Route.useSearch();
  const { number: dayNumber } = Route.useParams();
  const { agenda } = Route.useLoaderData();
  const { data: summitData } = useQuery(getPublicSummitDataOptions);

  // URL transformer to add A/B test UTM params
  const addUtmParams = (url: string): string =>
    appendAgendaTestUtm(url, agenda);

  // Get sorted days and the specific day for this page
  const { day, daySessions } = useMemo(() => {
    if (!summitData) return { day: null, daySessions: [] };

    const sortedDays = [...summitData.days].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    const targetDay = sortedDays[dayNumber - 1] ?? null;
    return {
      day: targetDay,
      daySessions: targetDay?.sessions ?? [],
    };
  }, [summitData, dayNumber]);

  // Check if there's an active stream to show for THIS day's sessions
  const activeLiveStream = useActiveLiveStream(daySessions);

  // Determine whether to show stream section
  const hasActiveStream = liveIdOverride ?? activeLiveStream?.videoId;

  // Check if there's any upcoming live session today
  const hasUpcomingLiveSession = useMemo(() => {
    const now = new Date();
    return daySessions.some((session) => {
      if (
        !session.liveUrl ||
        (session.sessionFormat !== "keynote" &&
          session.sessionFormat !== "presentation")
      ) {
        return false;
      }
      const startTime =
        session.startTimeUtc instanceof Date
          ? session.startTimeUtc
          : new Date(session.startTimeUtc);
      return startTime > now;
    });
  }, [daySessions]);

  // Show stream section if active OR if there's upcoming live content today
  const showStreamSection = hasActiveStream || hasUpcomingLiveSession;

  // When CTA is active, hide the agenda to focus on conversion
  const activeCTA = useLiveSummitCTA();
  const showAgenda = !activeCTA;

  if (!summitData || !day) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-gray-50">
      {/* Compact Header */}
      <div className="px-4 py-6 md:px-6 md:py-8">
        <div className="relative mx-auto max-w-5xl overflow-hidden rounded-xl shadow-lg shadow-purple-800/20">
          <CDNImage
            src="https://form-data.dreamcoachmatch.com/summit/summit-bg.jpg"
            alt=""
            width={1200}
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
            priority
            aria-hidden="true"
          />
          <div className="relative z-10 bg-purple-900/60 px-4 py-8 text-center md:px-6 md:py-12">
            <img
              src={ImageDcmLogoHorizontalWhiteUrl}
              alt="Dream Coach Match"
              className="mx-auto mb-3 h-4 w-auto opacity-80 md:mb-6 md:h-6"
            />
            <h1 className="mb-1 font-heading text-2xl tracking-tight text-white md:mb-2 md:text-4xl">
              {summitData.summit.title}
            </h1>
            <p className="text-lg font-medium text-purple-200 md:text-xl">
              Day {dayNumber}
            </p>
          </div>
        </div>
      </div>

      {/* Live Stream or Waiting Message - only show if relevant for this day */}
      {showStreamSection && (
        <>
          {hasActiveStream ? (
            <SummitLiveStream
              className="mx-auto max-w-5xl px-4 md:px-6"
              overrideVideoId={liveIdOverride}
              showCTA
              transformCtaUrl={addUtmParams}
            />
          ) : (
            <StreamWaitingMessage sessions={daySessions} />
          )}
        </>
      )}

      {/* Upsell Banner */}
      <div className="mx-auto mt-6 max-w-5xl px-4 md:mt-8 md:px-6">
        <SummitLifetimeAccessBanner transformUrl={addUtmParams} />
      </div>

      {/* Day Agenda - hidden when CTA is active to focus on conversion */}
      {showAgenda && (
        <div className="mx-auto mt-6 max-w-5xl px-4 md:mt-8 md:px-6">
          <SummitDayAccordion.Root
            type="multiple"
            defaultValue={agenda.variant === "expanded" ? [day.id] : []}
          >
            <SummitDayAccordion.Item value={day.id}>
              <SummitDayAccordion.Trigger>
                <div className="flex items-center gap-4">
                  <ICalCalendarIcon
                    date={day.date}
                    className="h-20 w-16 shrink-0 md:h-24 md:w-20"
                  />
                  <div className="text-left">
                    <h2 className="font-heading text-xl font-semibold text-gray-900 md:text-2xl">
                      {day.title}
                    </h2>
                    {day.description && (
                      <p className="mt-1 text-sm text-gray-600">
                        {day.description}
                      </p>
                    )}
                  </div>
                </div>
              </SummitDayAccordion.Trigger>
              <SummitDayAccordion.Content className="p-3 md:p-6">
                {daySessions.length === 0 ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
                    <p className="text-sm text-gray-600">
                      No sessions scheduled for this day yet. Check back soon!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 md:space-y-4">
                    {daySessions.map((session) => {
                      // For recording sessions, show the watch button with liveUrl
                      // For all other sessions, show no CTA (public page)
                      const footerVariant: SessionFooterVariant =
                        session.sessionFormat === "recording" && session.liveUrl
                          ? {
                              type: "viewing-recording",
                              recordingUrl: session.liveUrl,
                            }
                          : { type: "no-cta" };

                      return (
                        <SummitSessionCard
                          key={session.id}
                          session={session}
                          summitId={summitData.summit.id}
                          footerVariant={footerVariant}
                          userIsVip={true} // Allow watching recordings without VIP prompt
                          vipUpsellUrl=""
                        />
                      );
                    })}
                  </div>
                )}
              </SummitDayAccordion.Content>
            </SummitDayAccordion.Item>
          </SummitDayAccordion.Root>
        </div>
      )}

      <div className="mt-12 md:mt-16">
        <Footer />
      </div>
    </div>
  );
}
